import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './About.css';
import schoolPhoto1 from '../images/about1.jpg';
import schoolPhoto2 from '../images/about2.jpg';
import schoolPhoto3 from '../images/about3.jpg';
import nurseryImg from '../images/nursery_room.jpg';
import pix14Img from '../images/pix14.jpg';
import pix19Img from '../images/pix19.jpg';
import proprietressPhoto from '../images/proprietress.jpg';
import bannerPhoto from '../images/banner.jpg';
import brochure from '../files/brochure.pdf';

const STATS = [
  { value: '15+', label: 'Years of Excellence' },
  { value: '500+', label: 'Alumni' },
  { value: '3', label: 'Curriculum Frameworks' },
  { value: '100%', label: 'Dedicated Staff' },
];

const FACILITIES = [
  {
    img: schoolPhoto1,
    title: 'Interactive Classrooms',
    text:
      'Our classrooms are designed with vibrant décor, flexible seating, and modern teaching aids that make learning an exciting daily experience. Every detail encourages creativity, collaboration, and critical thinking.',
  },
  {
    img: schoolPhoto2,
    title: 'Well-Stocked Library',
    text:
      'At the heart of Twinkle Toes Schools is our library — a treasure trove of knowledge fuelling young imaginations. From storybooks to reference materials, it is a cornerstone of our learning environment.',
  },
  {
    img: schoolPhoto3,
    title: 'Safe Playground',
    text:
      'Beyond academics, we believe in the power of play for holistic development. Our playground is a safe, dynamic space where children build physical fitness, social skills, and genuine teamwork.',
  },
];

const CORE_VALUES = [
  {
    letter: 'I',
    title: 'Integrity',
    text: 'We hold ourselves to the highest standard of honesty in every interaction, teaching children to lead with character.',
  },
  {
    letter: 'P',
    title: 'Passion',
    text: 'Learning is most powerful when fuelled by genuine love for discovery. We keep that fire alive in every student.',
  },
  {
    letter: 'E',
    title: 'Excellence',
    text: 'We set the bar high — and give every child the tools, encouragement, and time to clear it.',
  },
  {
    letter: 'T',
    title: 'Team Spirit',
    text: 'Great things are built together. Collaboration, empathy, and respect form the backbone of life at Twinkle Toes.',
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, visible];
}

function Reveal({ children, className = '', delay = 0 }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`about-reveal ${visible ? 'about-reveal--in' : ''} ${className}`}
      style={{ '--reveal-delay': `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function CountUp({ target, suffix = '' }) {
  const [count, setCount] = useState(() => parseInt(target, 10) || target);
  const [ref, visible] = useInView(0.3);
  useEffect(() => {
    if (!visible) return;
    const num = parseInt(target, 10);
    if (isNaN(num)) { setCount(target); return; }
    let start = 0;
    const duration = 1200;
    const step = Math.ceil(num / (duration / 16));
    const id = setInterval(() => {
      start += step;
      if (start >= num) { setCount(num); clearInterval(id); }
      else setCount(start);
    }, 16);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  return (
    <span ref={ref}>
      {typeof count === 'number' ? count : target}{suffix}
    </span>
  );
}

function About() {
  return (
    <div className="about-page">

      {/* ═══════════════════════════════════════════
          HERO — full-bleed image, gradient to bottom
          ═══════════════════════════════════════════ */}
      <section className="about-hero" aria-label="About Twinkle Toes Schools">
        <div className="about-hero-photo" style={{ backgroundImage: `url(${bannerPhoto})` }} />
        <div className="about-hero-veil" />
        <div className="about-hero-content">
          <p className="about-hero-kicker">Our Story</p>
          <h1 className="about-hero-h1">
            A Heritage Built<br className="about-hero-br" /> on Purpose
          </h1>
          <p className="about-hero-sub">
            Founded with the belief that every child is extraordinary, Twinkle Toes Schools
            has spent fifteen years turning that belief into results.
          </p>
          <div className="about-hero-actions">
            <Link to="/admissions" className="about-btn about-btn--gold">Apply for Admission</Link>
            <a href={brochure} download className="about-btn about-btn--ghost">Download Brochure</a>
          </div>
        </div>
        <div className="about-hero-scroll" aria-hidden="true">
          <span />
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          STATS ROW — maroon band, animated numbers
          ═══════════════════════════════════════════ */}
      <section className="about-stats" aria-label="School at a glance">
        {STATS.map((s, i) => {
          const num = parseInt(s.value, 10);
          const suffix = s.value.replace(String(num), '');
          return (
            <div key={s.label} className="about-stat">
              <strong>
                <CountUp target={num} suffix={suffix} />
              </strong>
              <span>{s.label}</span>
            </div>
          );
        })}
      </section>

      {/* ═══════════════════════════════════════════
          MISSION + VISION — 2-col, gold left accent
          ═══════════════════════════════════════════ */}
      <section className="about-mv-section">
        <div className="about-mv-inner">
          <Reveal className="about-mv-left">
            <h2 className="about-section-h2">Where We Come<br />From &amp; Where<br />We're Going</h2>
            <p className="about-mv-lead">
              Since 2016, Twinkle Toes Schools has sat at the intersection of academic excellence and genuine human care — a school that measures success not just in grades, but in confidence, character, and joy.
            </p>
          </Reveal>
          <div className="about-mv-right">
            <Reveal className="about-mv-card" delay={120}>
              <div className="about-mv-accent" />
              <h3>Our Mission</h3>
              <p>To develop future leaders by providing quality learning opportunities that shape confident, creative young minds ready to meet the challenges of a complex world.</p>
            </Reveal>
            <Reveal className="about-mv-card" delay={220}>
              <div className="about-mv-accent" />
              <h3>Our Vision</h3>
              <p>To be an innovative, world-class provider of quality education — inspiring creativity, leadership, and excellence in young Nigerians who will shape tomorrow's Africa.</p>
            </Reveal>
          </div>
        </div>
        <div className="about-mv-photo-strip">
          <img src={nurseryImg} alt="Students at work in an interactive classroom" loading="lazy" />
          <img src={pix14Img} alt="Library reading nook" loading="lazy" />
          <img src={pix19Img} alt="Outdoor play and physical education" loading="lazy" />
          <img src={schoolPhoto2} alt="Students engaging in activities" loading="lazy" />
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          PROPRIETRESS — editorial 2-col, quote pull
          ═══════════════════════════════════════════ */}
      <section className="about-proprietress" aria-label="Message from the Proprietress">
        <div className="about-proprietress-inner">
          <Reveal className="about-proprietress-photo-col">
            <div className="about-proprietress-photo-wrap">
              <img
                src={proprietressPhoto}
                alt="Dr Abah Evangeline — Proprietress of Twinkle Toes Schools"
                loading="lazy"
              />
            </div>
            <div className="about-proprietress-tag">
              <strong>Dr Abah Evangeline</strong>
              <span>Proprietress &amp; Founder</span>
            </div>
          </Reveal>

          <Reveal className="about-proprietress-text-col" delay={140}>
            <p className="about-proprietress-label">A Word from the Founder</p>
            <h2 className="about-section-h2">Leading with<br />Heart &amp; Vision</h2>
            <p>
              With over 15 years dedicated to education, Dr. Abah Evangeline built Twinkle Toes Schools
              on a conviction: every child, given the right environment, will exceed every expectation placed on them.
            </p>
            <p>
              Under her leadership, the school has become a beacon of excellence in Orozo — blending world-class
              curricula with the warmth of a true family school, where staff know every child by name.
            </p>
            <blockquote className="about-proprietress-quote">
              <span className="about-quote-mark" aria-hidden="true">"</span>
              Empowering children today is the most important investment we can make in tomorrow.
              <span className="about-quote-mark about-quote-mark--close" aria-hidden="true">"</span>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          CORE VALUES — horizontal letter+title list,
          no rounded icons, no identical card grid
          ═══════════════════════════════════════════ */}
      <section className="about-values" aria-label="Core values">
        <Reveal className="about-values-header">
          <h2 className="about-section-h2">What We Stand For</h2>
          <p className="about-values-sub">
            Four principles that guide every decision, every lesson, and every interaction at Twinkle Toes.
          </p>
        </Reveal>
        <div className="about-values-grid">
          {CORE_VALUES.map((v, i) => (
            <Reveal key={v.title} className="about-value-item" delay={i * 90}>
              <div className="about-value-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</div>
              <div className="about-value-body">
                <h3>{v.title}</h3>
                <p>{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          FACILITIES — alternating image + text rows
          ═══════════════════════════════════════════ */}
      <section className="about-facilities" aria-label="School facilities">
        <Reveal className="about-facilities-header">
          <h2 className="about-section-h2">Where Learning<br />Comes to Life</h2>
          <p className="about-facilities-sub">
            Every space at Twinkle Toes is designed with purpose — to spark curiosity, foster collaboration,
            and make every child feel genuinely at home.
          </p>
        </Reveal>
        {FACILITIES.map((fac, i) => (
          <Reveal key={fac.title} className={`about-facility-row ${i % 2 !== 0 ? 'about-facility-row--flip' : ''}`}>
            <div className="about-facility-img">
              <img src={fac.img} alt={fac.title} loading="lazy" />
            </div>
            <div className="about-facility-text">
              <h3>{fac.title}</h3>
              <p>{fac.text}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* ═══════════════════════════════════════════
          BROCHURE CTA — full maroon band
          ═══════════════════════════════════════════ */}
      <section className="about-cta" aria-label="Download brochure">
        <Reveal className="about-cta-inner">
          <div className="about-cta-text">
            <h2>Everything You Need to Know</h2>
            <p>Our school brochure covers programmes, fees, facilities, and the admissions process in full.</p>
          </div>
          <div className="about-cta-actions">
            <a href={brochure} download className="about-btn about-btn--gold">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download Brochure
            </a>
            <Link to="/contact" className="about-btn about-btn--outline-white">Get in Touch</Link>
          </div>
        </Reveal>
      </section>

    </div>
  );
}

export default About;
